import config from '../../config';
import { Api, ApiConfig } from '../api';

export type ValueLedgerEnvironment = 'prod' | 'staging' | 'test' | 'local' | string;

export interface ValueLedgerLedgerParams {
  rangeId?: string | null;
  environment?: ValueLedgerEnvironment | null;
  workspaceId?: string | null;
}

export interface ValueLedgerRefreshRequest extends ValueLedgerLedgerParams {
  reason?: string | null;
}

export interface ValueLedgerOutcomeDetailParams {
  environment?: ValueLedgerEnvironment | null;
  workspaceId?: string | null;
}

export interface ValueLedgerRefreshResponse {
  snapshotId?: string | null;
  status: string;
  rangeId?: string | null;
  requestedAt?: string | null;
  [key: string]: unknown;
}

export interface ValueLedgerSnapshotStatus {
  snapshotId: string;
  status: string;
  generatedAt?: string | null;
  errorCode?: string | null;
  errorMessage?: string | null;
  reasonCode?: string | null;
  [key: string]: unknown;
}

export interface ValueLedgerRangeOption {
  rangeId: string;
  label?: string | null;
  startAt?: string | null;
  endAt?: string | null;
  timezone?: string | null;
  snapshotId?: string | null;
  status?: string | null;
  generatedAt?: string | null;
  createdAt?: string | null;
  runStartedAt?: string | null;
  sourceRangeStartAt?: string | null;
  sourceRangeEndAt?: string | null;
  errorCode?: string | null;
  errorMessage?: string | null;
  emptyReason?: string | null;
  isCurrentCycle?: boolean | null;
}

export interface ValueLedgerFilterItem {
  code?: string | null;
  label?: string | null;
  count: number;
}

export interface ValueLedgerWorkLocation {
  code?: string | null;
  label?: string | null;
  source?: string | null;
  confidence?: string | null;
  evidenceCount?: number | null;
  toolCallCount?: number | null;
  messageCount?: number | null;
  operationCounts?: {
    read?: number | null;
    write?: number | null;
    check?: number | null;
    other?: number | null;
    total?: number | null;
  } | null;
  [key: string]: unknown;
}

export interface ValueLedgerApiOutcome {
  outcomeId: string;
  outcomeNo?: string | number | null;
  sequence?: number | null;
  title?: string | null;
  summary?: string | null;
  outcomeType?: string | null;
  outcomeTypeLabel?: string | null;
  businessArea?: string | null;
  businessAreaLabel?: string | null;
  businessModule?: string | null;
  businessModuleLabel?: string | null;
  acceptanceStatus?: string | null;
  acceptanceStatusLabel?: string | null;
  cardTitle?: string | null;
  cardDescription?: string | null;
  sourceTaskTitle?: string | null;
  sourceTaskTime?: string | null;
  sourceTaskRef?: string | null;
  workLocations?: ValueLedgerWorkLocation[];
  whereBustlyWorked?: ValueLedgerWorkLocation[];
  detail?: Record<string, any> | null;
  [key: string]: unknown;
}

export interface ValueLedgerApiOverviewTaskArea {
  scenarioLabel?: string | null;
  taskAreaLabel?: string | null;
  taskCount?: number | null;
  activeMemberCount?: number | null;
  participantCount?: number | null;
  participant_count?: number | null;
  representativeWork?: string | null;
  sourceTaskIds?: string[] | null;
  [key: string]: unknown;
}

export interface ValueLedgerApiOverviewInsightCard {
  title?: string | null;
  description?: string | null;
  [key: string]: unknown;
}

export interface ValueLedgerApiOverviewInsightGroup {
  title?: string | null;
  description?: string | null;
  checklist?: Array<string | { text?: string | null; label?: string | null; }> | null;
  items?: Array<string | { text?: string | null; label?: string | null; }> | null;
  [key: string]: unknown;
}

export interface ValueLedgerApiOverviewChampion {
  name?: string | null;
  taskCount?: number | null;
  focus?: string | null;
  description?: string | null;
  [key: string]: unknown;
}

export interface ValueLedgerApiOverviewInsights {
  efficiencyHighlights?: ValueLedgerApiOverviewInsightCard[] | null;
  efficiencySignals?: ValueLedgerApiOverviewInsightCard[] | null;
  improvements?: ValueLedgerApiOverviewInsightCard[] | null;
  standardWorkflow?: ValueLedgerApiOverviewInsightGroup | null;
  workflowToStandardize?: ValueLedgerApiOverviewInsightGroup | null;
  firstWorkflowToStandardize?: ValueLedgerApiOverviewInsightGroup | null;
  reviewSuggestion?: ValueLedgerApiOverviewInsightGroup | null;
  operatingRhythm?: ValueLedgerApiOverviewInsightGroup | null;
  champions?: ValueLedgerApiOverviewChampion[] | null;
  championUsers?: ValueLedgerApiOverviewChampion[] | null;
  nextCycleFocus?: ValueLedgerApiOverviewInsightCard[] | null;
  nextCycleGoals?: ValueLedgerApiOverviewInsightCard[] | null;
  recommendedGoals?: ValueLedgerApiOverviewInsightCard[] | null;
  [key: string]: unknown;
}

export interface ValueLedgerApiOverview {
  headline?: string | null;
  description?: string | null;
  keyMetrics?: {
    userStartedTaskCount?: number | null;
    activeMemberCount?: number | null;
    coreScenarioTaskCount?: number | null;
    coreScenarioTaskShare?: number | null;
    coreScenarioLabel?: string | null;
    [key: string]: unknown;
  } | null;
  valueJudgment?: {
    title?: string | null;
    description?: string | null;
    [key: string]: unknown;
  } | null;
  taskScenarioBreakdown?: ValueLedgerApiOverviewTaskArea[] | null;
  taskScenarios?: ValueLedgerApiOverviewTaskArea[] | null;
  insights?: ValueLedgerApiOverviewInsights | null;
  aiInsights?: ValueLedgerApiOverviewInsights | null;
  [key: string]: unknown;
}

export interface ValueLedgerApiLedger {
  status: 'ready' | 'empty' | string;
  reasonCode?: string | null;
  ctaAction?: string | null;
  snapshotId?: string | null;
  schemaVersion?: string | null;
  generatedAt?: string | null;
  ledgerGeneratedAt?: string | null;
  ledgerRefreshSchedule?: {
    frequency?: string | null;
    localTime?: string | null;
    internalTriggerLocalTime?: string | null;
    timezone?: string | null;
    source?: string | null;
  } | null;
  currentGeneration?: Record<string, unknown> | null;
  workspace?: {
    workspaceId?: string | null;
    workspaceName?: string | null;
    workspaceAvatar?: string | null;
    initials?: string | null;
    viewerRole?: string | null;
    ledgerTitle?: string | null;
    lastGeneratedAt?: string | null;
    timezone?: string | null;
    [key: string]: unknown;
  } | null;
  range?: {
    availableRanges?: ValueLedgerRangeOption[];
    selectedRangeId?: string | null;
    rangeLabel?: string | null;
    rangeStartAt?: string | null;
    rangeEndAt?: string | null;
    timezone?: string | null;
    status?: string | null;
    generatedAt?: string | null;
    sourceRangeStartAt?: string | null;
    sourceRangeEndAt?: string | null;
    emptyReason?: string | null;
    isCurrentCycle?: boolean | null;
    [key: string]: unknown;
  } | null;
  summary?: {
    userTaskCount?: number | null;
    memberTaskCount?: number | null;
    bustlyOutcomeCount?: number | null;
    visibleOutcomeCount?: number | null;
    valueCalculationStatus?: string | null;
    valueInputsMissing?: string[] | null;
    totalDisplayCostMicrousd?: number | null;
    totalTokens?: number | null;
    [key: string]: unknown;
  } | null;
  filters?: {
    outcomeTypeFilters?: ValueLedgerFilterItem[];
    businessAreaFilters?: ValueLedgerFilterItem[];
    acceptanceStatusFilters?: ValueLedgerFilterItem[];
    [key: string]: unknown;
  } | null;
  overview?: ValueLedgerApiOverview | null;
  outcomeOverview?: ValueLedgerApiOverview | null;
  outcomes?: ValueLedgerApiOutcome[];
  [key: string]: unknown;
}

const compactParams = (params: Record<string, string | undefined>) =>
  Object.fromEntries(Object.entries(params).filter(([, value]) => Boolean(value)));

const workspaceHeaders = (workspaceId?: string | null) => (
  workspaceId ? { headers: { 'X-Workspace-Id': workspaceId } } : undefined
);

interface LedgerCacheEntry {
  value?: ValueLedgerApiLedger;
  promise?: Promise<ValueLedgerApiLedger>;
  expiresAt: number;
}

export class ValueLedgerService {
  private api: Api;
  private ledgerCache = new Map<string, LedgerCacheEntry>();
  private ledgerTtlMs = 30000;

  constructor(apiConfig: ApiConfig) {
    this.api = new Api(apiConfig);
  }

  getCachedLedger(params: ValueLedgerLedgerParams = {}): ValueLedgerApiLedger | null {
    const key = this.ledgerCacheKey(params);
    const cached = this.ledgerCache.get(key);
    if (!cached?.value || cached.expiresAt <= Date.now()) {
      if (cached && cached.expiresAt <= Date.now()) {
        this.ledgerCache.delete(key);
      }
      return null;
    }
    return cached.value;
  }

  async getLedger(params: ValueLedgerLedgerParams = {}): Promise<ValueLedgerApiLedger> {
    const key = this.ledgerCacheKey(params);
    const cached = this.ledgerCache.get(key);
    if (cached && cached.expiresAt > Date.now()) {
      if (cached.value) {
        return cached.value;
      }
      if (cached.promise) {
        return cached.promise;
      }
    } else if (cached) {
      this.ledgerCache.delete(key);
    }

    const promise = this.api.get<ValueLedgerApiLedger>('/admin/api/value-ledger', {
      params: compactParams({
        range_id: params.rangeId || undefined,
        environment: params.environment || undefined,
      }),
      ...workspaceHeaders(params.workspaceId),
    }).then((value) => {
      if (this.ledgerCache.get(key)?.promise !== promise) {
        return value;
      }
      if (value.status === 'ready') {
        this.ledgerCache.set(key, {
          value,
          expiresAt: Date.now() + this.ledgerTtlMs,
        });
      } else {
        this.ledgerCache.delete(key);
      }
      return value;
    }).catch((error) => {
      if (this.ledgerCache.get(key)?.promise === promise) {
        this.ledgerCache.delete(key);
      }
      throw error;
    });

    this.ledgerCache.set(key, {
      promise,
      expiresAt: Date.now() + this.ledgerTtlMs,
    });
    return promise;
  }

  async refresh(request: ValueLedgerRefreshRequest = {}): Promise<ValueLedgerRefreshResponse> {
    const body = {
      rangeId: request.rangeId || undefined,
      range_id: request.rangeId || undefined,
      environment: request.environment || undefined,
      reason: request.reason || undefined,
    };

    const response = await this.api.post<ValueLedgerRefreshResponse>(
      '/admin/api/value-ledger/refresh',
      body,
      workspaceHeaders(request.workspaceId)
    );
    this.clearLedgerCache(request.workspaceId);
    return response;
  }

  async getSnapshot(
    snapshotId: string,
    params: { workspaceId?: string | null } = {}
  ): Promise<ValueLedgerSnapshotStatus> {
    return this.api.get(`/admin/api/value-ledger/snapshots/${snapshotId}`, workspaceHeaders(params.workspaceId));
  }

  async getOutcomeDetail(
    outcomeId: string,
    params: ValueLedgerOutcomeDetailParams = {},
  ): Promise<ValueLedgerApiOutcome> {
    return this.api.get(`/admin/api/value-ledger/outcomes/${encodeURIComponent(outcomeId)}/detail`, {
      params: compactParams({
        environment: params.environment || undefined,
      }),
      ...workspaceHeaders(params.workspaceId),
    });
  }

  clearLedgerCache(workspaceId?: string | null): void {
    if (!workspaceId) {
      this.ledgerCache.clear();
      return;
    }

    const prefix = `${workspaceId}\u001f`;
    Array.from(this.ledgerCache.keys()).forEach((key) => {
      if (key.startsWith(prefix)) {
        this.ledgerCache.delete(key);
      }
    });
  }

  clearLedgerCacheEntry(params: ValueLedgerLedgerParams = {}): void {
    this.ledgerCache.delete(this.ledgerCacheKey(params));
  }

  private ledgerCacheKey(params: ValueLedgerLedgerParams): string {
    return [
      params.workspaceId || '',
      params.environment || '',
      params.rangeId || '',
    ].join('\u001f');
  }
}

export const valueLedgerService = new ValueLedgerService({
  baseURL: config.services.auth.apiHost,
  headers: { 'Content-Type': 'application/json' },
});
